import { useState, useEffect, useRef } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { startGetProgressValues } from "../../actions/ui";
import { useDispatchApp } from "../../lib/redux";
import { off } from "firebase/database";
import { logoPlatform } from "../../utils/string";
import { TASK_ALERT_PROGRESS_TRACK_BG } from "../../utils/colors";

const LinearLoaderWithTime = ({
  isAlert = false,
  label,
  labels = [],
  labelSide = "top",
  time,
  sx,
  sxLabel,
  progress: progressValue,
  progressID,
  preAddProgress = 2,
  autoIncrement = false,
  onProgressChange,
  platform,
}) => {
  const [reference, setReference] = useState(null);
  const [progress, setProgress] = useState(progressValue || 0);
  const lastReportedProgressRef = useRef(null);

  const dispatch = useDispatchApp();
  const hasProgressValue = typeof progressValue === "number";

  const getCurrentLabel = () => {
    if (labels.length === 0) return "";

    //Get label based on progress, must be distributed to cover 100% of the progress.
    const stepWidth = 100 / labels.length;
    if (progress === 100) return labels[labels.length - 1];
    let currentLabel = "";
    for (let i = 0; i < labels.length; i++) {
      if (progress < (i + 1) * stepWidth) {
        currentLabel = labels[i];
        break;
      }
    }
    return currentLabel;
  };

  const onUpdateProgress = (data = {}) => {
    const { step, totalSteps } = data;

    if (!step || !totalSteps) return;

    const newProgress = (step / totalSteps) * 100;

    if (newProgress < progress || newProgress >= 100) return;

    setProgress((step / totalSteps) * 100);
  };

  useEffect(() => {
    if (hasProgressValue) return;
    if (!progressID) return;

    dispatch(
      startGetProgressValues({
        progressID,
        onReferenceAvailable: (ref) => setReference(ref),
        onCallbackUpdateProgress: onUpdateProgress,
      })
    );

    setTimeout(() => {
      setProgress((prevProgress) => prevProgress + preAddProgress);
    }, [2000]);

    setTimeout(() => {
      setProgress((prevProgress) => prevProgress + preAddProgress);
    }, [4000]);

    return () => {
      if (reference) {
        off(reference);
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (hasProgressValue && !autoIncrement) return;
    if (progressID) return;
    const maxProgress = 99; //Max progress value
    const baseIncrement = 100 / (time / 1000); //Increment value based on time
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= maxProgress) {
          return prevProgress;
        }

        //Slow down the increment as it approaches 100%
        const slowDownFactor = (100 - prevProgress) / 100;
        const increment = baseIncrement * slowDownFactor;

        return Math.min(prevProgress + increment, maxProgress);
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [time]);

  useEffect(() => {
    if (!hasProgressValue) return;

    // When auto-incrementing, treat the prop as a high-water seed only.
    // Blindly syncing every parent float back into local state + onProgressChange
    // creates an infinite update loop with persisted/elapsed progress.
    if (autoIncrement) {
      setProgress((prev) => (progressValue > prev ? progressValue : prev));
      return;
    }

    setProgress(progressValue);
    // eslint-disable-next-line
  }, [progressValue, hasProgressValue, autoIncrement]);

  // Notify parent of progress changes
  useEffect(() => {
    if (!onProgressChange) return;
    if (lastReportedProgressRef.current === progress) return;
    lastReportedProgressRef.current = progress;
    onProgressChange(progress);
    // eslint-disable-next-line
  }, [progress]);

  return (
    <Box sx={{ width: "100%", ...sx }}>
      {labelSide === "top" && (
        <Typography
          variant="body1"
          align="center"
          sx={{ color: "#000", ...sxLabel }}
        >
          {label ? label : getCurrentLabel()}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              borderRadius: isAlert ? 999 : 5,
              height: isAlert ? 6 : undefined,
              backgroundColor: isAlert
                ? TASK_ALERT_PROGRESS_TRACK_BG
                : undefined,
              "& .MuiLinearProgress-bar": {
                borderRadius: isAlert ? 999 : undefined,
              },
            }}
          />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="#000">{`${Math.round(
            progress
          )}%`}</Typography>
        </Box>
      </Box>
      {labelSide === "bottom" && (
        <Box
          sx={{
            textAlign: isAlert ? "left" : "center",
          }}
        >
          <Typography
            variant="body1"
            component="span"
            sx={{
              color: "#000",
              ...sxLabel,
            }}
          >
            {platform && (
              <Avatar
                src={logoPlatform(platform)}
                variant="square"
                sx={{
                  width: 20,
                  height: 20,
                  display: "inline-flex",
                  mr: 1.5,
                  verticalAlign: "text-bottom",
                  ".MuiAvatar-img": { objectFit: "contain" },
                }}
              />
            )}
            {label ? label : getCurrentLabel()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LinearLoaderWithTime;
